import React, { useState, useCallback, useMemo } from "react";
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import draftToHtml from "draftjs-to-html";

interface RichTextEditorProps {
  initialContent: string;
  onSave: (htmlContent: string) => Promise<void> | void;
  title: string;
  placeholder?: string;
  saveButtonText?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  onSave,
  title,
  placeholder = "Start typing here...",
  saveButtonText = "Save",
}) => {
  const theme = useSelector((state: RootState) => state.theme);

  const [editorState, setEditorState] = useState(() => {
    try {
      if (initialContent) {
        const blocksFromHTML = convertFromHTML(initialContent);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        return EditorState.createWithContent(contentState);
      }
      return EditorState.createEmpty();
    } catch (error) {
      console.error("Error initializing editor state:", error);
      return EditorState.createEmpty();
    }
  });

  const [saving, setSaving] = useState(false);

  const toolbarOptions = useMemo(
    () => ({
      options: [
        "inline",
        "blockType",
        "list",
        "textAlign",
        "link",
        "image",
        "remove",
        "history",
      ],
      inline: { options: ["bold", "italic", "underline", "strikethrough"] },
      blockType: {
        options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote"],
      },
      list: { options: ["unordered", "ordered"] },
      textAlign: { options: ["left", "center", "right", "justify"] },
    }),
    []
  );

  const handleEditorChange = useCallback((newEditorState: EditorState) => {
    setEditorState(newEditorState);
  }, []);

  const handleSaveClick = useCallback(async () => {
    try {
      setSaving(true);
      const rawContent = convertToRaw(editorState.getCurrentContent());
      const htmlContent = draftToHtml(rawContent);
      await onSave(htmlContent);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  }, [editorState, onSave]);

  return (
    <Paper
      sx={{ p: 3, backgroundColor: theme.primary_colors[800], boxShadow: 0 }}
    >
      <Typography variant="h5" sx={{ mb: 2, color: theme.primary_colors[700] }}>
        {title}
      </Typography>
      <Box
        sx={{
          ".rdw-editor-toolbar": {
            backgroundColor: theme.primary_colors[900],
            borderColor: theme.primary_colors[50],
            color: theme.primary_colors[700],
          },
          ".rdw-option-wrapper, .rdw-dropdown-wrapper": {
            backgroundColor: theme.primary_colors[900],
            color: theme.primary_colors[700],
            borderColor: theme.primary_colors[50],
          },
          ".rdw-option-wrapper:hover, .rdw-option-active": {
            backgroundColor: theme.primary_colors[600],
          },
          ".rdw-editor-main": {
            minHeight: "200px",
            backgroundColor: theme.primary_colors[900],
            color: theme.primary_colors[700],
            border: `1px solid ${theme.primary_colors[300]}`,
            padding: "8px",
          },
          ".DraftEditor-root": {
            color: theme.primary_colors[700],
            "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
              color: theme.primary_colors[700],
            },
            "& a": { color: theme.primary_colors[10] },
            lineHeight: 1.7,
            fontSize: { xs: "0.875rem", md: "1rem" },
          },
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          toolbar={toolbarOptions}
          placeholder={placeholder}
        />
      </Box>
      <Button
        variant="contained"
        sx={{
          mt: 3,
          backgroundColor: theme.primary_colors[500],
          cursor: "pointer",
        }}
        onClick={handleSaveClick}
        disabled={saving}
        startIcon={
          saving ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {saving ? "Saving..." : saveButtonText}
      </Button>
    </Paper>
  );
};

export default RichTextEditor;
